import { useState, useCallback, useEffect, useRef, useId } from 'react';
import { Box, Divider } from '@/components/primitives';
import { Input } from '@/components/primitives';
import { useMutation } from '@/api/mutation';
import { StyledDialog } from '@/components/dialogs';
import { AvatarEditor, type CropData } from '@/components/forms';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { signIn } from '@/auth/authData';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { reportMutationError } from '@/api/reportMutationError';
import { uploadFileToS3 } from '@/api';
import { updateProfile, getMe, removeAvatar } from '@/api';
import { ContactMethodsEditor } from '../ContactMethodsEditor';
import { PasswordChangeForm } from '../PasswordChangeForm';
import { testIds } from '@/test/testIds';
import {
  useFileRefRefetch,
  useUnconfirmedSave,
  extractFileDecline,
  clearLocalEcho,
  type UnconfirmedSave,
} from '@/features/files';
import { usePendingAvatarStore, usePendingAvatarKey } from '../../pendingAvatar';
import { dialogMessages } from '@/constants';

export interface ProfileEditDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * ProfileEditDialog - Modal for editing user profile
 *
 * Uses StyledDialog with "clean" appearance (no colored header, floating close button).
 * Actions are inline - no footer.
 *
 * Feedback ownership (#821): a dialog component's own `useFeedbackZone()`
 * resolves to its PARENT layer (StyledDialog's layer provider is in its render
 * output), so the mutations live in `ProfileEditForm`, rendered inside the
 * StyledDialog: their errors, and the avatar acknowledgements (the dialog
 * stays open after them), land in this dialog's own band.
 */
export function ProfileEditDialog({ open, onClose }: ProfileEditDialogProps) {
  const { user } = useCurrentUser();
  // Lifted from the form: a pending profile save blocks closing.
  const [saving, setSaving] = useState(false);

  // Refresh user data. /api/auth/me/ does NOT return the token (audit H4) —
  // it must be re-attached from the current user, or setUser persists
  // `token: undefined` and every subsequent request sends "Token undefined".
  const refreshUser = useCallback(async () => {
    const token = user?.token;
    if (token) {
      const updatedUser = await getMe(token);
      signIn({ ...updatedUser, token });
    }
  }, [user?.token]);

  // Transition discovery (file-handling §14.3.7): while the avatar is still
  // processing server-side, re-read /me on a backoff so the finished rendition
  // arrives on its own. Without this the single post-save refetch returns the
  // pre-processing FileRef and nothing ever replaces it until a reload.
  // Best-effort (#885 D5): a failed poll must not escape as an unhandled
  // rejection; the next tick re-reads.
  // I4 (#885a-4): a re-read that hands back the avatar the save REPLACED (a
  // lagging replica) is not the new file's outcome, so polling goes on. The
  // record lives here, not in the form: the dialog stays mounted in the app
  // shell after it closes, and so does the poll.
  const avatarSave = useUnconfirmedSave();
  const staleAvatar = avatarSave.unconfirmed(user?.avatar);
  useFileRefRefetch(
    [user?.avatar],
    () => {
      refreshUser().catch(() => {});
    },
    { unconfirmed: staleAvatar },
  );

  // The header's pending pick (#885 D6) is only an echo for a PROCESSING
  // avatar: once the server says ready or failed, the file layer ignores the
  // echo anyway, so drop the key and its bytes rather than let a later upload
  // (another device) borrow this pick. Only a terminal read of the NEW file
  // counts (#885a-4 R4): a stale re-read of the old failed avatar keeps it.
  const pendingKey = usePendingAvatarKey(user);
  const avatarStatus = user?.avatar?.status;
  useEffect(() => {
    if (pendingKey && !staleAvatar && (avatarStatus === 'ready' || avatarStatus === 'failed')) {
      clearLocalEcho(pendingKey);
      usePendingAvatarStore.getState().clear();
    }
  }, [pendingKey, avatarStatus, staleAvatar]);

  // Handlers
  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
    }
  }, [saving, onClose]);

  if (!user) return null;

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      size="standard"
      title="Edit Profile"
      closeDisabled={saving}
      footer={null}
      DialogProps={{ 'data-testid': testIds.profile.editDialog }}
    >
      {/* The form follows the user (#1336): when the same token starts answering
          as the surviving login, a remount refills the name fields from the
          survivor, so a blur afterwards cannot PUT the folded login's name. */}
      <ProfileEditForm
        key={user.id}
        open={open}
        refreshUser={refreshUser}
        onSavingChange={setSaving}
        avatarSave={avatarSave}
      />
    </StyledDialog>
  );
}

/**
 * Rendered INSIDE the StyledDialog, so its `useFeedbackZone` resolves to the
 * profile dialog's own band.
 */
function ProfileEditForm({
  open,
  refreshUser,
  onSavingChange,
  avatarSave,
}: {
  open: boolean;
  refreshUser: () => Promise<void>;
  onSavingChange: (saving: boolean) => void;
  avatarSave: UnconfirmedSave;
}) {
  const { user } = useCurrentUser();
  const userId = user?.id;
  const zone = useFeedbackZone();
  const uploadSource = useId();

  // Form state
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');

  // Remove avatar mutation
  const removeAvatarMutation = useMutation({
    mutationFn: removeAvatar,
    onSuccess: (updatedUser) => {
      // Use the write's OWN authoritative response — NOT a follow-up getMe().
      // /api/auth/me/ can read a lagging replica and still return the
      // just-removed avatar, leaving it on screen until a manual reload. Clear
      // the avatar fields explicitly so removal always reflects immediately.
      signIn({ ...updatedUser, avatar: null, token: user?.token ?? updatedUser.token });
      // No avatar: nothing left for the header's pending pick to echo, and
      // no save left to confirm.
      usePendingAvatarStore.getState().clear();
      avatarSave.clear();
    },
    // 5xx + transport errors are surfaced centrally by routeMutationError, in
    // this dialog's band (#1174; same predicate useApiMutation uses) — pushing
    // here too would double it.
    onError: (error) => {
      reportMutationError(zone, error, { message: 'Failed to remove avatar.' });
    },
  });

  // Sync form state ONLY on the closed→open transition (audit M10).
  // Seeding on every `user` change meant the post-blur save (whose response
  // replaces the user object) reset BOTH name fields while the user was
  // still typing in the second one.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (!seededRef.current && user) {
      seededRef.current = true;
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
    }
  }, [open, user]);

  // Update profile mutations: the avatar save and the name blur are two actions
  // on one PUT, so each has its own instance and key (#1628-d1): the other's
  // success never clears this one's failure.
  const updateAvatarMutation = useMutation({
    mutationFn: updateProfile,
    // Seed from the write's OWN response (#885 D5, as removeAvatar does): it
    // is the full profile (same schema as /me), so the new avatar FileRef —
    // `processing` while the server crops — is in the cache at once and the
    // poll above takes it from there. No awaited /me: a failed or lagging
    // re-read must never turn an accepted save into a failure. The response
    // carries no token (audit H4); re-attach the session's.
    onSuccess: (updatedUser) => {
      signIn({ ...updatedUser, token: user?.token ?? updatedUser.token });
    },
    // 5xx + transport errors are surfaced centrally by routeMutationError, in
    // this dialog's band (#1174).
    // A file decline (422 {code, message}) is AvatarEditor's to say, inline
    // under the picture, and nowhere else (#885 D1).
    onError: (error) => {
      if (extractFileDecline(error)) return;
      reportMutationError(zone, error, { message: 'Failed to update profile.' });
    },
  });

  // Same options as updateAvatarMutation (see the comments there).
  const updateNameMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (updatedUser) => {
      signIn({ ...updatedUser, token: user?.token ?? updatedUser.token });
    },
    onError: (error) => {
      if (extractFileDecline(error)) return;
      reportMutationError(zone, error, { message: 'Failed to update profile.' });
    },
  });

  const isLoading = updateAvatarMutation.isPending || updateNameMutation.isPending;
  useEffect(() => {
    onSavingChange(isLoading);
    // A forced unmount mid-save must not leave the outer dialog closeDisabled.
    return () => onSavingChange(false);
  }, [isLoading, onSavingChange]);

  const handleAvatarSave = useCallback(async (file: File, crop: CropData) => {
    // #885 D1: AvatarEditor says only a file decline, inline; every other
    // failure is this caller's to say, once. The upload is a plain API call
    // (nothing routes it centrally), so it is said here. The mutation's own
    // failures are said by its onError (reportMutationError). Either way the
    // rejection propagates so AvatarEditor keeps the old picture.
    let fileKey: string;
    try {
      fileKey = await uploadFileToS3(file);
    } catch (error) {
      if (!extractFileDecline(error)) {
        zone.push(dialogMessages.profileEdit.avatarUploadFailed, 'error', { source: uploadSource });
      }
      throw error;
    }
    // The upload is its own action (#1628-c3): an earlier failure of it goes now,
    // whatever the profile PUT below does.
    zone.clearSource(uploadSource);
    // The avatar this save replaces: a re-read that hands it back is stale.
    const replaced = user?.avatar;
    await new Promise<void>((resolve, reject) => {
      updateAvatarMutation.mutate(
        { avatar: { key: fileKey, crop } },
        {
          onSuccess: (updatedUser) => {
            avatarSave.markSaved(replaced, updatedUser.avatar);
            // The save was accepted (#885 D4). The header shows the pick
            // through the local echo while the server processes it (D6):
            // uploadFileToS3 registered the bytes under this key.
            if (userId !== undefined) usePendingAvatarStore.getState().record(userId, fileKey);
            zone.push('Avatar updated.', 'success');
            resolve();
          },
          onError: (error) => reject(error),
        }
      );
    });
  }, [updateAvatarMutation, zone, userId, user?.avatar, avatarSave, uploadSource]);

  const handleAvatarDelete = useCallback(async () => {
    await new Promise<void>((resolve, reject) => {
      removeAvatarMutation.mutate(undefined, {
        onSuccess: () => {
          zone.push('Avatar removed.', 'success');
          resolve();
        },
        onError: (error) => reject(error),
      });
    });
  }, [removeAvatarMutation, zone]);

  const handleNameBlur = useCallback(() => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (trimmedFirst && trimmedLast &&
        (trimmedFirst !== user?.firstName || trimmedLast !== user?.lastName)) {
      updateNameMutation.mutate({ firstName: trimmedFirst, lastName: trimmedLast });
    }
  }, [firstName, lastName, user?.firstName, user?.lastName, updateNameMutation]);

  if (!user) return null;

  return (
    <>
      {/* Avatar section */}
      <Box display="flex" justifyContent="center" marginBottom="lg">
        <AvatarEditor
          shape="circle"
          fileRef={user.avatar}
          initials={`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
          onSave={handleAvatarSave}
          // A processing or failed avatar is still an avatar the user may want
          // gone — gate removal on the FileRef existing, not on a ready URL.
          onDelete={user.avatar ? handleAvatarDelete : undefined}
          disabled={isLoading || removeAvatarMutation.isPending}
        />
      </Box>

      {/* Name fields - side by side, inline save on blur */}
      <Box display="flex" gap="ms" marginBottom="lg">
        <Input
          label="First Name"
          // Equal halves; the Input root's min-width: 0 lets each shrink (#1304).
          style={{ flex: '1 1 0' }}
          data-testid={testIds.profile.firstNameInput}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={handleNameBlur}
          disabled={isLoading}
        />
        <Input
          label="Last Name"
          style={{ flex: '1 1 0' }}
          data-testid={testIds.profile.lastNameInput}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onBlur={handleNameBlur}
          disabled={isLoading}
        />
      </Box>

      {/* Contact Methods */}
      <ContactMethodsEditor
        userId={user.id}
        emails={user.emails || []}
        phones={user.phones || []}
        onRefresh={refreshUser}
        disabled={isLoading}
      />

      <Divider style={{ marginBottom: 24 }} />

      {/* Password */}
      <PasswordChangeForm />
    </>
  );
}
